<template>
    <DModal
        :show="showModal"
        :title="__('tools.activity_logs.changes.title', { subject: subject })"
        is-big
        @update:show="$emit('update:showModal', $event)"
        @close="closeModal()"
    >
        <DCard class="overflow">
            <DCardBody>
                <DTable
                    :items="items"
                    :fields="fields"
                >
                    <template #old="{item}">
                        <td>
                            <pre v-if="item.isObject">{{ item.old }}</pre>
                            <span v-else>{{ item.old }}</span>
                        </td>
                    </template>
                    <template #new="{item}">
                        <td>
                            <pre v-if="item.isObject">{{ item.new }}</pre>
                            <span v-else>{{ item.new }}</span>
                        </td>
                    </template>
                </DTable>
            </DCardBody>
        </DCard>
    </DModal>
</template>

<script setup>
import { computed } from 'vue'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    subject: {
        type: String,
        default: null
    },
    changes: {
        type: Object,
        default: () => { return {} }
    }
})

const emit = defineEmits(['close-modal', 'update:showModal'])

const fields = [
    { key: 'field', label: __('tools.activity_logs.changes.field') },
    { key: 'old', label: __('tools.activity_logs.changes.old') },
    { key: 'new', label: __('tools.activity_logs.changes.new') }
]

const items = computed(() => {
    // `attributes` is a plain object, so the previous `?.length` check was always
    // undefined and never returned early. Both halves are defaulted because an entry can
    // carry only one of them: a Spatie `created` event logs `attributes` with no `old`, and a
    // deleted pending invoice item logs `old` with nothing on the "new" side (CIO-10182).
    const attributes = props.changes.attributes ?? {}
    const old = props.changes.old ?? {}
    // The union of both sides, `attributes` first so an ordinary update keeps its field order.
    // Keying off `attributes` alone left an `old`-only entry with an empty table even though
    // the action button had offered one.
    const fieldsWithChanges = [...new Set([...Object.keys(attributes), ...Object.keys(old)])]

    if (fieldsWithChanges.length === 0) {
        return []
    }

    const items = []

    for (const field of fieldsWithChanges) {
        items.push({
            field: field,
            old: isObject(old[field]) ? JSON.stringify(old[field], null, '\t') : (old[field] ?? ''),
            new: isObject(attributes[field]) ? JSON.stringify(attributes[field], null, '\t') : (attributes[field] ?? ''),
            isObject: isObject(old[field]) || isObject(attributes[field])
        })
    }

    return items
})

const isObject = (item) => {
    return item !== null && typeof item === 'object'
}
const closeModal = () => {
    emit('close-modal')
}
</script>

<style lang="scss" scoped>
.overflow {
   overflow: auto;
}
</style>
